import { useMutation } from '@tanstack/react-query';
import { ChartColumn, Loader2 } from 'lucide-react';
import { useDataProvider, useNotify, useRecordContext } from 'ra-core';
import { type AdminDataProvider } from '../../api/adminDataProvider';
import { IconButtonWithTooltip } from '../../components/admin/icon-button-with-tooltip';
import { saveSpreadsheet } from '../../utils/spreadsheet/save';

/** Downloads the report of the current nudge as a CSV file */
export const GetNudgeReportButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const getReport = useMutation({
    mutationFn: async () => {
      const id = record?.id;
      if (!id) {
        throw new Error('Record context is not available');
      }

      const res = await dataProvider.getNudgesReport(id);

      await saveSpreadsheet({
        format: 'csv',
        data: res.data.map((r) => ({
          ['Nudge ID']: r.nudgeId,
          Categories: r.categories,
          ['Nudge Title']: r.nudgeTitle,
          ['Nudge Created On']: r.nudgeCreatedOn,
          ['Nudge Last Edited On']: r.nudgeLastEditedOn,
          ['Employee ID']: r.employeeId,
          ['Employee Internal ID']: r.employeeInternalId ?? undefined,
          ['Employee Name']: r.employeeName,
          ['Job Title']: r.jobTitle,
          Department: r.department,
          Region: r.region,
          Units: r.units,
          ['Total Slides']: r.totalSlides,
          ['First Accessed On']: r.firstAccessedOn ?? undefined,
          ['Last Accessed On']: r.lastAccessedOn ?? undefined,
          ['Accessed Slides']: r.accessedSlides,
          Completed: r.completed,
          Rating: r.rating ?? undefined,
          ['Is Interesting Rating']: r.isInterestingRating ?? undefined,
        })),
        basename: `${new Date()
          .toISOString()
          .substring(0, '2000-01-01'.length)} - Nudges report`,
      });
    },
    onError: (error) => {
      notify('moonstar.nudges.report.error', {
        type: 'error',
        messageArgs: { message: error.message },
      });
    },
  });

  return (
    <IconButtonWithTooltip
      label="moonstar.nudges.actions.report"
      onClick={() => getReport.mutate()}
      disabled={getReport.isPending}
    >
      {getReport.isPending ? (
        <Loader2 className="animate-spin" />
      ) : (
        <ChartColumn />
      )}
    </IconButtonWithTooltip>
  );
};
